import LocationBar from 'location-bar';
import forEach from 'lodash/forEach';
import remove from 'lodash/remove';
import first from 'lodash/first';
import split from 'lodash/split';
import last from 'lodash/last';
import { v4 as uuid } from 'uuid';
import 'url-search-params-polyfill';
import { getBusinessGroup, getCustomerSegment, getTechnology, isBusinessContext, isLocal } from './util';
import { ADDRESS_ID_PARAM, LOCATION_ID_PARAM } from '../shared/params/params';
import {
  Segment,
  ServiceType,
  TBroadbandProductData,
  BroadbandPayloadItem,
  OfferPayloadItem,
  PayloadItem,
} from '../components/containers/BroadbandPlanCard/types';
import {
  BILL_FREQUENCY,
  MAXIMUM_STANDARD_SPEED,
  ONLINE_SIGNUP,
  SPEED,
  STARTER_SPEED,
  TECHNOLOGY,
} from '../components/containers/BroadbandPlanCard/constants';

/**
 * This is the helper file to communication between components via URL.
 */

// Instantiate LocationBar
const locationBar = new LocationBar();
locationBar.start({
  pushState: true,
});

interface Callback {
  name: string;
  value: Function;
}

const listeners: Array<Function | Callback> = [];
locationBar.onChange(() => {
  forEach(listeners, listener => {
    const functionCallback = listener as Callback;
    if (functionCallback.value != null) {
      functionCallback.value();
    } else {
      const classCallback = listener as Function;
      classCallback();
    }
  });
});

// for tests only
export const updateUrl = (param: string) => {
  locationBar.update(`?${param}`, { replace: true, trigger: true });
};

export const subscribe = (listener: Function | Callback) => {
  listeners.push(listener);
};

export const subscribeFunc = (listener: Callback) => {
  const indexof = listeners.findIndex(c => {
    return c.name === listener.name;
  });

  if (indexof !== -1) {
    listeners[indexof] = listener;

    return;
  }

  subscribe(listener);
};

export const unsubscribe = (listener: Function) => {
  remove(listeners, listener);
};

export const getParam = (queryString: string, param: string) => {
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get(param);
};

export const pushParam = (param: string, value: string, reset?: boolean, title?: string) => {
  const params = new URLSearchParams(reset ? '' : window.location.search);
  params.set(param, value);
  window.history.replaceState({}, title, `${window.location.pathname}?${params}`);
};

export const removeParam = (param: string, reset?: boolean, title?: string) => {
  const params = new URLSearchParams(reset ? '' : window.location.search);
  params.delete(param);
  window.history.replaceState({}, title, `${window.location.pathname}?${params}`);
};

export const clearAllParams = () => {
  const params = new URLSearchParams('');
  window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
};

export const clearAllParamsByKey = (key: string) => {
  const params = Array.from(new URLSearchParams(window.location.search).entries());

  params.forEach(param => {
    const paramkey = first(param);

    if (paramkey.includes(key)) {
      removeParam(paramkey);
    }
  });
};

export const getModelId = (dataFile: string = 'p30') => {
  const modelIdParam = getParam(window.location.search, 'modelId');
  return isLocal() ? dataFile : modelIdParam || last(split(window.location.pathname, '/'));
};

/**
 * Returns the URL for api calls
 * @param apiEndpoint - relative api endpoint
 * @param queryString - the string containing query params
 * @param useLocalApi - use local api endpoint mock for development
 * @param dataFile - the name of the local json file with data
 */
export const buildUrl = (
  apiEndpoint: string,
  queryString: string = '',
  useLocalApi: boolean = true,
  dataFile: string = 'p30'
) => {
  return isLocal() && useLocalApi
    ? `http://localhost:8080/data/${getModelId(dataFile)}.json`
    : `${apiEndpoint}${queryString}`;
};

export const buildQuery = (params: { [key: string]: string | boolean | number }) => {
  const queryString = Object.keys(params)
    .map(key => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');

  return queryString ? `?${queryString}` : '';
};

export const getTileUrl = (tileUrl: string, coord: google.maps.Point, zoom: Number) =>
  tileUrl
    .replace('{x}', coord.x.toString())
    .replace('{y}', coord.y.toString())
    .replace('{z}', zoom.toString());

export const buildDeviceListingUrl = (tahiApiUrl: string, deviceListing: string, useLocalApi: boolean = true) =>
  buildUrl(
    `${tahiApiUrl}${deviceListing}`,
    buildQuery({
      ...(isBusinessContext() && { audience: 'business' }),
    }),
    useLocalApi,
    'productListing'
  );

export const buildDeviceDetailsUrl = (
  tahiApiUrl: string,
  deviceListing: string,
  modelId: string,
  useLocalApi: boolean = true
) =>
  buildUrl(
    `${tahiApiUrl}${deviceListing}/${modelId}`,
    buildQuery({
      ...(isBusinessContext() && { audience: 'business' }),
    }),
    useLocalApi
  );

/**
 * Builds availability API url, based on url addressId param and customer segment
 * @param string
 * @param string
 * @returns url string
 */
export const buildAvailabilityUrl = (apiEndpoint: string, addressId?: string, customerSegment?: Segment) => {
  const addressIdParam = getParam(window.location.search, ADDRESS_ID_PARAM);
  if (!addressId && !addressIdParam) {
    return null;
  }
  const query = `?segment=${customerSegment ?? getCustomerSegment()}`;
  const url = buildUrl(
    `${apiEndpoint}/${addressId || addressIdParam}/availability${query}`,
    '',
    true,
    `broadband/address-availability/${addressIdParam}/availability`
  );
  return url;
};

export const buildQualificationUrl = (apiEndpoint: string, locationId?: string, customerSegment?: Segment) => {
  const locationIdParam = getParam(window.location.search, LOCATION_ID_PARAM);
  if (!locationId && !locationIdParam) {
    return null;
  }

  const url = buildUrl(
    `${apiEndpoint}/sales/v3/qualification/qualify`,
    buildQuery({
      businessGroup: getBusinessGroup(customerSegment),
      salesChannel: ONLINE_SIGNUP,
      locationId: locationId || locationIdParam,
    }),
    false,
    ``
  );
  return url;
};

export const buildQualifyProductsUrl = (apiEndpoint: string, locationId: string, customerSegment?: Segment) => {
  if (!locationId) {
    return null;
  }

  const url = buildUrl(
    `${apiEndpoint}/sales/v3/qualification/products`,
    buildQuery({
      businessGroup: getBusinessGroup(customerSegment),
      salesChannel: ONLINE_SIGNUP,
      locationId,
    }),
    false,
    ``
  );
  return url;
};

/**
 * Build a broadband payload URL for Checkout v2.
 *
 * @param baseUrl URL for Checkout
 * @param segment Business or Consumer
 * @param coreLogicTui Chorus identifer eg A123456789
 * @param locationId Tahi locationId - a guid e.g. 12345678-1234-1234-1234-123456789012
 * @param coreProduct CoreProductKey from Tahi database
 * @param eventTrackingId Optional tracking ID for dataLayer
 * @returns URL which populates the Checkout v2 cart with the given product
 */
export const buildCheckoutUrl = (
  baseUrl: string,
  segment: Segment,
  coreLogicTui: string,
  locationId: string,
  coreProduct: string,
  eventTrackingId?: string
) => {
  const serviceId = uuid();

  const payload = {
    salesUser: {
      salesPerson: ONLINE_SIGNUP,
      salesChannel: ONLINE_SIGNUP,
    },
    leadProduct: 'broadband',
    items: [
      {
        id: serviceId,
        type: 'broadband',
        coreProductKey: coreProduct,
        serviceAddress: {
          coreLogicTui,
          locationId,
        },
      } as BroadbandPayloadItem,
    ] as PayloadItem[],
    eventTrackingId,
  };

  // TODO: 254275 below code to be removed or refactored for any offer
  if (window.location.href.includes('2degrees.nz/broadband/joining-credit')) {
    payload.items.push({
      type: 'offer',
      offerId: '2d_Credit_100',
      service: serviceId,
    } as OfferPayloadItem);
  }

  const encoded = btoa(JSON.stringify(payload));

  return `${baseUrl}${segment === Segment.Business ? '/business' : ''}/broadband?cart=${encoded}`;
};

const mapDownloadSpeedToParameter = (broadbandData: TBroadbandProductData) => {
  // Download speed is non numeric for wbbV2 products
  if (Number.isNaN(Number(broadbandData.downSpeed))) {
    switch (true) {
      case broadbandData.downSpeed === SPEED.wbb4G:
        return SPEED.wbb4G;
      case broadbandData.downSpeed === SPEED.wbb5G:
        return SPEED.wbb5G;
      case broadbandData.downSpeed === SPEED.wbbRCG:
        return SPEED.wbbRCG;
      default:
        return SPEED.unknown;
    }
  }

  const speed: number = Number(broadbandData.downSpeed);
  switch (true) {
    case speed === STARTER_SPEED:
      return SPEED.starter;
    case speed >= MAXIMUM_STANDARD_SPEED:
      return SPEED.max;
    default:
      return SPEED.standard;
  }
};

export const buildVocusUrl = (baseUrl: string, broadbandData: TBroadbandProductData, coreLogicTui: string) => {
  const params = {
    coreLogicTui,
    businessGroup: getBusinessGroup(broadbandData.segment),
    technology: getTechnology(broadbandData.serviceType),
    speed:
      broadbandData.serviceType === ServiceType.Hyperfibre
        ? broadbandData.downSpeed
        : mapDownloadSpeedToParameter(broadbandData),
    // data entitlement is only applied to wbbV2 products
    ...(getTechnology(broadbandData.serviceType) === TECHNOLOGY.wireless && { data: broadbandData?.dataEntitlement }),
    billFrequency: BILL_FREQUENCY.month,
  };

  const checkoutParams = new URLSearchParams(params);

  return `${baseUrl}?${checkoutParams}`;
};
